import { getEarliestFollowUpDate } from "../../../shared/lib/ev-workflow-policy.ts";
import type { EvWorkstationProject } from "../types/index.ts";

export type EvWorkflowSortKey =
  | "score"
  | "delta"
  | "stage"
  | "owner"
  | "projectFollowUp"
  | "warmIntroFollowUp"
  | "nextAction";

export type EvWorkflowSortDir = "asc" | "desc";

const STAGE_ORDER: Record<string, number> = {
  lead: 0,
  group_created: 1,
  call_completed: 2,
  passed_ic: 3,
  qualified_for_sale: 4,
  contract_shared: 5,
  closed_onboarding: 6,
  ngmi: 7,
  no_token: 8,
  no_for_now: 8,
};

function compareItems(
  a: EvWorkstationProject,
  b: EvWorkstationProject,
  key: EvWorkflowSortKey,
  dir: EvWorkflowSortDir
): number {
  let cmp = 0;
  switch (key) {
    case "score": {
      const sa = Number(a.project.current_alpha_score ?? 0);
      const sb = Number(b.project.current_alpha_score ?? 0);
      cmp = sa - sb;
      break;
    }
    case "delta": {
      const da = Number(a.project.delta_alpha_score ?? 0);
      const db = Number(b.project.delta_alpha_score ?? 0);
      cmp = da - db;
      // Break ties with rank_change (more reliably populated)
      if (cmp === 0) {
        const ra = Number(a.project.rank_change ?? 0);
        const rb = Number(b.project.rank_change ?? 0);
        cmp = ra - rb;
      }
      break;
    }
    case "stage":
      cmp = (STAGE_ORDER[a.workflow.stage] ?? 99) - (STAGE_ORDER[b.workflow.stage] ?? 99);
      break;
    case "owner": {
      const oa = a.workflow.ownerEmail ?? "zzz";
      const ob = b.workflow.ownerEmail ?? "zzz";
      cmp = oa.localeCompare(ob);
      break;
    }
    case "projectFollowUp": {
      const fa = a.workflow.projectFollowUpDate ?? a.workflow.followUpDate ?? "9999-12-31";
      const fb = b.workflow.projectFollowUpDate ?? b.workflow.followUpDate ?? "9999-12-31";
      cmp = fa.localeCompare(fb);
      break;
    }
    case "warmIntroFollowUp": {
      const fa =
        getEarliestFollowUpDate(a.workflow.warmIntroFollowUpDate, a.workflow.projectFollowUpDate) ??
        "9999-12-31";
      const fb =
        getEarliestFollowUpDate(b.workflow.warmIntroFollowUpDate, b.workflow.projectFollowUpDate) ??
        "9999-12-31";
      cmp = fa.localeCompare(fb);
      break;
    }
    case "nextAction": {
      const na = a.workflow.nextAction ?? "zzz";
      const nb = b.workflow.nextAction ?? "zzz";
      cmp = na.localeCompare(nb);
      break;
    }
  }

  return dir === "desc" ? -cmp : cmp;
}

export function orderEvWorkflowItems(
  items: EvWorkstationProject[],
  sortKey: EvWorkflowSortKey | null,
  sortDir: EvWorkflowSortDir
): EvWorkstationProject[] {
  if (!sortKey) {
    return items;
  }

  return [...items].sort((a, b) => compareItems(a, b, sortKey, sortDir));
}
